"use client";

import { useState, useCallback, useRef } from "react";
import { useSettings } from "@/lib/settings-context";
import api, { type LocaleCode } from "@/lib/api/client";
import { getCacheKey, getCachedAudio, cacheAudio } from "@/lib/audio-cache";

const SLOW_SPEED = 0.6;

/**
 * Fetch a single text at a given speed — check IndexedDB, then fetch.
 * If flyMachineId is provided, pin the request to that Fly machine
 * so it hits the warm L1 memory cache from BE TTS warming.
 */
async function fetchOne(
  text: string,
  speed: number,
  lang: LocaleCode,
  flyMachineId?: string | null
): Promise<void> {
  const key = getCacheKey({ text, speed, lang });
  try {
    const cached = await getCachedAudio(key);
    if (cached) return;
  } catch {
    // ignore cache check failures
  }
  try {
    const { response } = await api.POST("/api/tts", {
      body: { text, speed, lang },
      parseAs: "stream",
      headers: flyMachineId
        ? { "fly-force-instance-id": flyMachineId }
        : undefined,
    });
    if (response.ok) {
      const blob = await response.blob();
      await cacheAudio(key, blob);
    }
  } catch {
    // Silently ignore — on-demand TTS + browser fallback work fine
  }
}

/**
 * Reactive TTS caching driven by backend `tts_ready` SSE events.
 * When the BE signals that a phrase is cached, `markServerReady` immediately
 * opens the playback gate and kicks off background IndexedDB caching
 * (guaranteed cache hits — no batching or delay needed).
 *
 * If `flyMachineId` is provided, all /api/tts requests are pinned to that
 * Fly machine via the `fly-force-instance-id` header, ensuring L1 memory
 * cache hits on the same machine that did the TTS warming.
 */
export function useTtsPrefetch(lang: LocaleCode, flyMachineId?: string | null) {
  const { tts } = useSettings();
  const [readyTexts, setReadyTexts] = useState<Set<string>>(new Set());
  const inFlightRef = useRef<Set<string>>(new Set());

  /**
   * Mark texts as ready immediately (BE cache is warm) and kick off
   * background IndexedDB caching. No batching or delay needed since
   * the BE has already cached the audio — every fetch is a guaranteed hit.
   */
  const markServerReady = useCallback(
    (texts: string[]) => {
      const newTexts = texts.filter(
        (t) => t && !readyTexts.has(t) && !inFlightRef.current.has(t)
      );
      if (!newTexts.length) return;

      // Immediately mark as ready so playback gates open
      setReadyTexts((prev) => {
        const next = new Set(prev);
        for (const t of newTexts) next.add(t);
        return next;
      });

      // Background: pull from /api/tts (guaranteed cache hit) → IndexedDB
      for (const text of newTexts) {
        inFlightRef.current.add(text);
        Promise.allSettled([
          fetchOne(text, tts.speed, lang, flyMachineId),
          tts.speed !== SLOW_SPEED
            ? fetchOne(text, SLOW_SPEED, lang, flyMachineId)
            : Promise.resolve(),
        ]);
      }
    },
    [lang, tts.speed, readyTexts, flyMachineId]
  );

  const isReady = useCallback(
    (text: string) => readyTexts.has(text),
    [readyTexts]
  );

  return { markServerReady, isReady, readyCount: readyTexts.size };
}
